import React from "react";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All students" },
  { value: "withQr", label: "With QR" },
  { value: "withoutQr", label: "Without QR" },
  { value: "active", label: "Active QR true" },
  { value: "inactive", label: "Active QR false" },
];

export default function StudentQrTable({
  loading,
  onDeleteStudentQr,
  onPageChange,
  onRowsPerPageChange,
  onSearchTextChange,
  onStatusFilterChange,
  onUploadForStudent,
  page,
  rowsPerPage,
  searchText,
  statusFilter,
  students,
  totalCount,
  rowOperationState,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", lg: "center" }}
        sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Student QR Registry
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Search by student name or UID, then upload, replace, or delete QR
            images.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            label="Search students"
            placeholder="Name or UID"
            value={searchText}
            onChange={(event) => onSearchTextChange(event.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <InputLabel id="student-qr-status-filter-label">Filter</InputLabel>
            <Select
              labelId="student-qr-status-filter-label"
              label="Filter"
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value)}
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      <TableContainer>
        <Table sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              <TableCell>Student Name</TableCell>
              <TableCell>UID</TableCell>
              <TableCell>QR Image Preview</TableCell>
              <TableCell>Active QR Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 6 }}
                  >
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Loading students...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ py: 7, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      No students matched the current filters.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Adjust your search or upload new QR images in bulk.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              students.map((student) => {
                const rowState = rowOperationState[student.id] || {};
                const busy = Boolean(rowState.loading);

                return (
                  <TableRow hover key={student.id}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {student.name || "Unnamed student"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {student.uid || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 240 }}>
                      {student.qrImageUrl ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            component="img"
                            src={student.qrImageUrl}
                            alt={`${student.name || student.uid} QR`}
                            sx={{
                              width: 72,
                              height: 72,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              bgcolor: "grey.50",
                            }}
                          />
                          <Box>
                            <Link
                              href={student.qrImageUrl}
                              target="_blank"
                              rel="noreferrer"
                              underline="hover"
                              variant="body2"
                            >
                              Open image
                            </Link>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mt: 0.75 }}
                            >
                              Linked in Firestore
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No QR image linked
                        </Typography>
                      )}

                      {busy && rowState.action !== "delete" && (
                        <Box sx={{ mt: 1.5, maxWidth: 220 }}>
                          <LinearProgress
                            variant="determinate"
                            value={rowState.progress || 0}
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            Uploading {rowState.progress || 0}%
                          </Typography>
                        </Box>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={student.activeqr ? "true" : "false"}
                        color={student.activeqr ? "success" : "default"}
                        variant={student.activeqr ? "filled" : "outlined"}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="flex-end"
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFileRoundedIcon />}
                          disabled={busy}
                          sx={{ borderRadius: 999 }}
                        >
                          {student.qrImageUrl ? "Replace" : "Upload"}
                          <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                onUploadForStudent(student, file);
                              }
                              event.target.value = "";
                            }}
                          />
                        </Button>

                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          disabled={busy || !student.qrImageUrl}
                          onClick={() => onDeleteStudentQr(student)}
                          sx={{ borderRadius: 999 }}
                        >
                          Delete
                        </Button>
                      </Stack>

                      {busy && rowState.action === "delete" && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Removing QR image...
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
}
